/**
 * Auth client — talks directly to Auth0's Authentication API for embedded
 * email/password signup + login (no Auth0 hosted UI), plus calls our
 * backend for the on-domain password reset flow.
 *
 * Social login is initiated by redirecting to /authorize?connection=...
 * (Google/Microsoft redirect to provider, no Auth0 page rendered).
 *
 * After embedded auth succeeds, the app uses the existing @auth0/auth0-react
 * SDK (`getAccessTokenSilently`) to obtain access tokens for backend calls.
 * The cross-origin endpoint sets the Auth0 session cookie on the custom
 * domain so the SDK picks up the session.
 */

import { LOCALE_STORAGE_KEY } from '@/i18n/config';

/** Best-effort read of the persisted landing/login locale; null if unavailable. */
function readStoredLocale(): string | undefined {
  try {
    return window?.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN as string;
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID as string;
const AUTH0_AUDIENCE = import.meta.env.VITE_AUTH0_AUDIENCE as string;
const AUTH0_DB_CONNECTION = 'Username-Password-Authentication';
const API_BASE = import.meta.env.VITE_API_SERVER_URL as string;

const REDIRECT_URI = `${window.location.origin}/auth-callback`;
const SIGNUP_INTENT_KEY = 'invirtus.signup_intent';
const AUTH_RETURN_PATH_KEY = 'invirtus.auth_return_path';

export const GOOGLE_CONNECTION = 'google-oauth2';

/**
 * Auth0 connection name for Microsoft / Entra ID login.
 *
 * Read from the environment because Auth0 connection names are immutable once
 * created and each environment is its own Auth0 tenant, so the same logical
 * connection can end up named differently (e.g. `microsoftdev` in dev). Defaults
 * to `microsoft`, which is the name staging and production should use.
 */
export const MICROSOFT_CONNECTION: string =
  (import.meta.env.VITE_AUTH0_MICROSOFT_CONNECTION as string | undefined) || 'microsoft';

/** An Auth0 connection name. Values are tenant-specific, hence not a literal union. */
export type SocialConnection = string;

export interface SignupIntent {
  plan?: string;
  currency?: string;
  invitation_token?: string;
  marketing_consent?: boolean;
  ts: number;
}

const SIGNUP_INTENT_TTL_MS = 30 * 60 * 1000;

export const signupIntent = {
  set(intent: Omit<SignupIntent, 'ts'>) {
    try {
      localStorage.setItem(
        SIGNUP_INTENT_KEY,
        JSON.stringify({ ...intent, ts: Date.now() }),
      );
    } catch {
      // localStorage may be blocked — silently degrade
    }
  },
  read(): SignupIntent | null {
    try {
      const raw = localStorage.getItem(SIGNUP_INTENT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as SignupIntent;
      if (!parsed.ts || Date.now() - parsed.ts > SIGNUP_INTENT_TTL_MS) {
        localStorage.removeItem(SIGNUP_INTENT_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  },
  clear() {
    try {
      localStorage.removeItem(SIGNUP_INTENT_KEY);
    } catch {
      // ignore
    }
  },
};

/**
 * Where to send the user back to if Auth0 returns an error (e.g. a
 * blocked personal email domain) instead of a successful callback.
 * Saved right before a social-login redirect, since the whole page
 * navigates away and React state doesn't survive that trip.
 */
export const authReturnPath = {
  save(path: string) {
    try {
      sessionStorage.setItem(AUTH_RETURN_PATH_KEY, path);
    } catch {
      // sessionStorage may be blocked — silently degrade
    }
  },
  read(): string | null {
    try {
      return sessionStorage.getItem(AUTH_RETURN_PATH_KEY);
    } catch {
      return null;
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
    } catch {
      // ignore
    }
  },
};

interface Auth0SignupBody {
  client_id: string;
  email: string;
  password: string;
  connection: string;
  given_name?: string;
  family_name?: string;
  name?: string;
}

export class AuthClientError extends Error {
  code?: string;
  description?: string;

  constructor(message: string, code?: string, description?: string) {
    super(message);
    this.code = code;
    this.description = description;
  }
}

/**
 * Auth0's PasswordStrengthError returns `description` as a nested
 * object describing every policy rule (length, containsAtLeast, etc.)
 * and which ones failed. Dropping that into a toast renders
 * `[object Object]`. This walks the tree and returns a concise sentence
 * that tells the user what to fix.
 *
 * Shape reference (Auth0 dbconnections/signup):
 *   { rules: [
 *       { code: 'lengthAtLeast', format: [12], verified: false },
 *       { code: 'containsAtLeast', format: [3,4],
 *         items: [{ code: 'upperCase', verified: false, message }, …],
 *         verified: false },
 *     ], verified: false }
 */
function formatAuth0PasswordError(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.code !== 'invalid_password') return null;
  const desc = d.description as Record<string, unknown> | undefined;
  const rules = Array.isArray(desc?.rules) ? (desc!.rules as any[]) : [];
  const failing: string[] = [];

  for (const rule of rules) {
    if (rule?.verified) continue;
    if (rule?.code === 'lengthAtLeast') {
      const min = Array.isArray(rule.format) ? rule.format[0] : undefined;
      failing.push(min ? `at least ${min} characters` : 'enough characters');
      continue;
    }
    if (rule?.code === 'containsAtLeast' && Array.isArray(rule.items)) {
      const missing = rule.items
        .filter((it: any) => !it?.verified && typeof it?.message === 'string')
        .map((it: any) => it.message as string);
      if (missing.length > 0) {
        failing.push(missing.join(', '));
      }
      continue;
    }
    if (typeof rule?.message === 'string') failing.push(rule.message);
  }

  const base = typeof d.message === 'string' ? d.message : 'Password is too weak';
  if (failing.length === 0) return base;
  return `${base}. Add ${failing.join('; ')}.`;
}

/**
 * Create a new user in the Auth0 database connection.
 * Does NOT log them in — caller follows up with `loginEmailPassword`.
 */
export async function signupEmailPassword(input: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}): Promise<void> {
  const body: Auth0SignupBody = {
    client_id: AUTH0_CLIENT_ID,
    email: input.email.trim().toLowerCase(),
    password: input.password,
    connection: AUTH0_DB_CONNECTION,
  };
  if (input.firstName) body.given_name = input.firstName;
  if (input.lastName) body.family_name = input.lastName;
  if (input.firstName || input.lastName) {
    body.name = `${input.firstName ?? ''} ${input.lastName ?? ''}`.trim();
  }

  const resp = await fetch(`https://${AUTH0_DOMAIN}/dbconnections/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    const passwordMsg = formatAuth0PasswordError(data);
    const message =
      passwordMsg ??
      (typeof data.description === 'string' ? data.description : undefined) ??
      data.message ??
      'Signup failed';
    throw new AuthClientError(
      message,
      data.code,
      typeof data.description === 'string' ? data.description : undefined,
    );
  }
}

/**
 * Embedded login via Auth0 cross-origin authentication.
 *
 * On success, Auth0 sets a session cookie on its custom domain. We then
 * trigger a silent /authorize hop using the SDK's `getAccessTokenSilently`
 * (or a manual /authorize redirect with prompt=none) to obtain tokens.
 *
 * For simplicity in the embedded flow, we use the second strategy: after
 * `/co/authenticate` succeeds, we redirect to /authorize with
 * `prompt=none&login_ticket=...` which lands back on our /auth-callback
 * with the access token in the URL fragment (handled by the Auth0 SDK).
 */
export async function loginEmailPassword(input: {
  email: string;
  password: string;
}): Promise<{ login_ticket: string; co_id?: string; co_verifier?: string }> {
  const resp = await fetch(`https://${AUTH0_DOMAIN}/co/authenticate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: AUTH0_CLIENT_ID,
      username: input.email.trim().toLowerCase(),
      password: input.password,
      realm: AUTH0_DB_CONNECTION,
      credential_type: 'http://auth0.com/oauth/grant-type/password-realm',
    }),
  });

  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    const description =
      typeof data.description === 'string' ? data.description : undefined;
    const errorDescription =
      typeof data.error_description === 'string' ? data.error_description : undefined;
    throw new AuthClientError(
      description ?? errorDescription ?? 'Wrong email or password',
      data.code || data.error,
      description ?? errorDescription,
    );
  }
  return resp.json();
}

// `completeEmbeddedLogin` was previously hand-rolling the /authorize
// redirect with its own state/nonce. That conflicts with @auth0/auth0-react
// which manages its own state in storage and rejects callbacks it didn't
// initiate ("Invalid state"). The right path is to call the SDK's
// loginWithRedirect with `login_ticket` in authorizationParams — the SDK
// then signs state/nonce, redirects, and validates on return.
//
// Pages call this hook helper directly via useAuth0(); the auth.service
// stays focused on Auth0 endpoints we hit with raw fetch (signup, ROPG,
// password reset).

/**
 * Initiate a social-login redirect. The user goes to Google/LinkedIn,
 * not to any Auth0-hosted page.
 */
export function redirectToSocialLogin(connection: SocialConnection): void {
  const state = crypto.randomUUID();
  const nonce = crypto.randomUUID();
  sessionStorage.setItem('auth0.state', state);
  sessionStorage.setItem('auth0.nonce', nonce);

  // Remember where the user was so that if Auth0 comes back with an
  // error (e.g. personal_email_blocked), /auth-callback can send them
  // back here instead of a hardcoded /login.
  authReturnPath.save(window.location.pathname + window.location.search);

  const params = new URLSearchParams({
    client_id: AUTH0_CLIENT_ID,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
    audience: AUTH0_AUDIENCE,
    scope: 'openid profile email',
    state,
    nonce,
    connection,
  });
  window.location.assign(
    `https://${AUTH0_DOMAIN}/authorize?${params.toString()}`,
  );
}

/** Request a password-reset email via our backend. Always resolves. */
export async function requestPasswordReset(email: string): Promise<void> {
  await fetch(`${API_BASE}/api/auth/password-reset-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim().toLowerCase() }),
  });
}

/** Confirm password reset using the token from the email link. */
export async function confirmPasswordReset(input: {
  token: string;
  newPassword: string;
}): Promise<void> {
  const resp = await fetch(`${API_BASE}/api/auth/password-reset-confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: input.token, new_password: input.newPassword }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new AuthClientError(
      data.detail || data.message || 'Could not reset password',
    );
  }
}

/** Hit /api/onboarding/finalize with the persisted intent. */
export async function finalizeOnboarding(input: {
  accessToken: string;
  plan_code: string;
  currency?: string;
  invitation_token?: string;
  marketing_consent?: boolean;
  /**
   * UI locale the visitor was on at signup. Carried into the new tenant's
   * `default_language` so landing-page -> tenant continuity holds for the
   * signup workflow. Defaults to the persisted landing locale. Ignored by the
   * backend for invitation acceptance (the invite carries its own language).
   */
  language?: string;
}): Promise<{
  next_action: 'dashboard' | 'checkout' | 'setup_card';
  checkout_url?: string;
  session_id?: string;
  redirect_path?: string;
  plan_code: string;
}> {
  const language = input.language ?? readStoredLocale();
  const resp = await fetch(`${API_BASE}/api/onboarding/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify({
      plan_code: input.plan_code,
      currency: input.currency ?? 'USD',
      invitation_token: input.invitation_token,
      marketing_consent: input.marketing_consent,
      language,
    }),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new AuthClientError(data.detail || 'Finalize failed');
  }
  return resp.json();
}
